import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Loader2, FileUp, Download, AlertTriangle, CheckCircle2 } from "lucide-react";

const CSV_TEMPLATE = `title,location,contract_type,sector,salary_min,salary_max,description,requirements,benefits,application_url
Senior Backend Engineer,Ebène,CDI,Tech,80000,120000,"Design and ship production-grade APIs in Python.","Python, FastAPI, MongoDB, 5+ years","Health insurance, remote-friendly",https://example.com/apply/123
Marketing Manager,Port-Louis,CDI,Marketing,60000,90000,"Lead the marketing team and drive growth.","5+ yrs marketing leadership","Bonus, training budget",
`;

export default function AdminBulkJobsDialog({ open, onOpenChange, employers, onUploaded }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [employerId, setEmployerId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "motravay_jobs_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!employerId) { toast.error(lang === "fr" ? "Sélectionnez un employeur" : "Pick an employer"); return; }
    if (!file) { toast.error(lang === "fr" ? "Sélectionnez un CSV" : "Pick a CSV file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("employer_id", employerId);
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/jobs/bulk-csv`, fd, { headers: { ...authHeader, "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(lang === "fr"
        ? `${r.data.created_count} offre(s) créée(s)`
        : `${r.data.created_count} job(s) created`);
      onUploaded && onUploaded(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Import échoué" : "Import failed"));
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setResult(null); setFile(null); } }}>
      <DialogContent className="max-w-xl" data-testid="admin-bulk-jobs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {lang === "fr" ? "Import en masse — CSV" : "Bulk upload jobs (CSV)"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{lang === "fr" ? "Employeur cible" : "Target employer"}</Label>
            <Select value={employerId} onValueChange={setEmployerId}>
              <SelectTrigger data-testid="admin-bulk-employer"><SelectValue placeholder={lang === "fr" ? "Choisir…" : "Choose…"} /></SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {(employers || []).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.company_name} ({e.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{lang === "fr" ? "Fichier CSV" : "CSV file"}</Label>
            <label className="mt-1 flex items-center gap-2 border-2 border-dashed border-slate-300 hover:border-[#1A8FE3] rounded-xl px-3 py-6 cursor-pointer transition-colors">
              <FileUp className="h-5 w-5 text-slate-500" />
              <span className="text-sm text-slate-700">{file ? file.name : (lang === "fr" ? "Cliquez pour sélectionner un .csv" : "Click to choose a .csv file")}</span>
              <input
                data-testid="admin-bulk-file"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              {lang === "fr"
                ? "Colonnes requises : title, location, contract_type, description"
                : "Required columns: title, location, contract_type, description"}
            </span>
            <button onClick={downloadTemplate} data-testid="admin-bulk-template" className="inline-flex items-center gap-1 text-[#1A8FE3] hover:underline">
              <Download className="h-3 w-3" />
              {lang === "fr" ? "Modèle CSV" : "CSV template"}
            </button>
          </div>

          {result && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 text-sm" data-testid="admin-bulk-result">
              <div className="inline-flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                {result.created_count} {lang === "fr" ? "créé(s)" : "created"}
              </div>
              {result.errors?.length > 0 && (
                <details className="text-xs">
                  <summary className="inline-flex items-center gap-1 text-amber-700 cursor-pointer">
                    <AlertTriangle className="h-3 w-3" /> {result.errors.length} {lang === "fr" ? "erreur(s)" : "error(s)"}
                  </summary>
                  <ul className="mt-1 ml-4 list-disc">
                    {result.errors.slice(0, 20).map((er, i) => (
                      <li key={i}>row {er.row}: {er.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            {lang === "fr" ? "Fermer" : "Close"}
          </Button>
          <Button data-testid="admin-bulk-submit" onClick={submit} disabled={uploading} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === "fr" ? "Importer" : "Import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
